import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
// import sensorCursor from 'assets/images/sensor5.png';
import { fetchS3ImageUrl } from '../FloorsMap/FloorsMap';
import sensorCursor from 'assets/images/targetdevice.png';
import targetCursor from 'assets/images/target.png';
import sensor from 'assets/images/sensor2.png';

const DeviceLocation = ({ floorSchemaKey, onPositionSelect }) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [hasPlacedMarker, setHasPlacedMarker] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!floorSchemaKey) {
        setImageUrl(null);
        setLoadError('No floor image configured for this device.');
        return;
      }

      setImageLoading(true);
      setLoadError('');
      try {
        const downloadUrl = await fetchS3ImageUrl(floorSchemaKey);
        if (!isMounted) return;

        if (downloadUrl) {
          setImageUrl(downloadUrl);
        } else {
          setImageUrl(null);
          setLoadError('No floor image configured for this device.');
        }
      } catch (error) {
        if (!isMounted) return;
        setImageUrl(null);
        setLoadError('Failed to load the floor image.');
      } finally {
        if (isMounted) {
          setImageLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [floorSchemaKey]);

  const handleImageClick = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top - 12) / rect.height) * 100;

    setMarkerPosition({ x, y });
    setHasPlacedMarker(true);
    if (onPositionSelect) {
      onPositionSelect({ x, y });
    }
  };

  const handleImageLoad = event => {
    setImageDimensions({
      width: event.target.naturalWidth,
      height: event.target.naturalHeight,
    });
  };

  return (
    <Box
      sx={{
        width: 'fit-content',
        height: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      {imageLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2">Loading floor image...</Typography>
        </Box>
      ) : imageUrl ? (
        <Box
          sx={{
            width: 'fit-content',
            height: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // overflow: 'hidden',
          }}>
          <Box
            sx={{
              position: 'relative',
              maxHeight: 'fit-content',
              width: 'fit-content',
              aspectRatio: imageDimensions
                ? `${imageDimensions.width} / ${imageDimensions.height}`
                : 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <Box
              className="floor-image"
              component="img"
              src={imageUrl}
              alt="floor map"
              onClick={handleImageClick}
              onLoad={handleImageLoad}
              sx={{
                maxWidth: '80vw', // ← cap to viewport
                maxHeight: '80vh', // ← cap to viewport
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 2,
                cursor: hasPlacedMarker
                  ? `url(${targetCursor}) 18 80, auto`
                  : `url(${sensorCursor}) 18 80, auto`,
              }}
            />
            {markerPosition && (
              <Box
                key={`${markerPosition.x}-${markerPosition.y}`}
                component="img"
                src={sensor}
                alt="sensor marker"
                sx={{
                  position: 'absolute',
                  left: `${markerPosition.x}%`,
                  top: `${markerPosition.y}%`,
                  width: '70px',
                  height: '60px',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                  animation: 'sensorMarkerGlow 2s ease-out',
                  '@keyframes sensorMarkerGlow': {
                    '0%': {
                      filter: 'drop-shadow(0 0 0 rgba(0, 140, 255, 0))',
                    },
                    '35%': {
                      filter: 'drop-shadow(0 0 20px rgba(0, 42, 255, 1))',
                    },
                    '100%': {
                      filter: 'drop-shadow(0 0 0 rgba(0, 140, 255, 0))',
                    },
                  },
                }}
              />
            )}
          </Box>
        </Box>
      ) : (
        <Typography variant="body1" color="text.secondary">
          {loadError || 'No floor image available.'}
        </Typography>
      )}
    </Box>
  );
};

export default DeviceLocation;
